/**
 * Draggable/clickable scroll-progress indicator for any horizontally
 * scrollable element. Markup contract:
 *
 *   <slider-progress-bar for="Slider-{{ id }}" role="scrollbar" tabindex="0" ...>
 *     <span class="slider-progress-bar__thumb"></span>
 *   </slider-progress-bar>
 *
 * `for` must match the id of the scrollable target.
 */
class SliderProgressBar extends HTMLElement {
  #target = null;
  #thumb = null;
  #resizeObserver = null;
  #dragController = null;

  connectedCallback() {
    this.#target = document.getElementById(this.getAttribute('for'));
    this.#thumb = this.querySelector('.slider-progress-bar__thumb');
    if (!this.#target || !this.#thumb) return;

    this.#target.addEventListener('scroll', this.#update, { passive: true });
    this.#resizeObserver = new ResizeObserver(this.#update);
    this.#resizeObserver.observe(this.#target);
    this.addEventListener('pointerdown', this.#handlePointerDown);
    this.addEventListener('keydown', this.#handleKeyDown);
    window.addEventListener('load', this.#update);

    this.#update();
  }

  disconnectedCallback() {
    this.#target?.removeEventListener('scroll', this.#update);
    this.#resizeObserver?.disconnect();
    this.#dragController?.abort();
    window.removeEventListener('load', this.#update);
  }

  #update = () => {
    const scrollable = this.#target.scrollWidth - this.#target.clientWidth;

    // Ignore sub-pixel rounding overflow from percentage-based flex layouts
    if (scrollable <= 1) {
      this.hidden = true;
      return;
    }

    this.hidden = false;

    const thumbPercent = (this.#target.clientWidth / this.#target.scrollWidth) * 100;
    const leftPercent = (this.#target.scrollLeft / scrollable) * (100 - thumbPercent);

    this.#thumb.style.width = `${thumbPercent}%`;
    this.#thumb.style.insetInlineStart = `${leftPercent}%`;
    this.setAttribute('aria-valuenow', String(Math.round((this.#target.scrollLeft / scrollable) * 100)));
  };

  #jumpTo(clientX) {
    const rect = this.getBoundingClientRect();
    const ratio = (clientX - rect.left) / rect.width;
    const scrollable = this.#target.scrollWidth - this.#target.clientWidth;
    this.#target.scrollLeft = Math.min(Math.max(ratio, 0), 1) * scrollable;
  }

  #startDrag(event) {
    event.preventDefault();
    this.#thumb.classList.add('slider-progress-bar__thumb--dragging');
    this.#dragController?.abort();
    this.#dragController = new AbortController();
    const { signal } = this.#dragController;

    const trackWidth = this.getBoundingClientRect().width;
    const startX = event.clientX;
    const startScrollLeft = this.#target.scrollLeft;
    const scrollable = this.#target.scrollWidth - this.#target.clientWidth;

    const onMove = (moveEvent) => {
      const ratio = (moveEvent.clientX - startX) / trackWidth;
      this.#target.scrollLeft = startScrollLeft + ratio * scrollable;
    };

    const stopDrag = () => {
      this.#thumb.classList.remove('slider-progress-bar__thumb--dragging');
      this.#dragController.abort();
    };

    document.addEventListener('pointermove', onMove, { signal });
    document.addEventListener('pointerup', stopDrag, { signal });
    document.addEventListener('pointercancel', stopDrag, { signal });
  }

  #handlePointerDown = (event) => {
    if (event.target === this.#thumb) {
      this.#startDrag(event);
      return;
    }
    this.#jumpTo(event.clientX);
  };

  #handleKeyDown = (event) => {
    if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;

    const step = this.#target.clientWidth * 0.8;

    if (event.key === 'ArrowRight') this.#target.scrollLeft += step;
    if (event.key === 'ArrowLeft') this.#target.scrollLeft -= step;
    if (event.key === 'Home') this.#target.scrollLeft = 0;
    if (event.key === 'End') this.#target.scrollLeft = this.#target.scrollWidth;

    event.preventDefault();
  };
}

if (!customElements.get('slider-progress-bar')) {
  customElements.define('slider-progress-bar', SliderProgressBar);
}
